import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { MatchConfig, PlayerAnim, PlayerState, TeamSide, Vec3, WorldSnapshot } from '../../core/contracts';
import { damp } from '../../core/math';
import { DUGOUT } from './pitch';
import { numberAtlas } from './textures';
import { angleLerp, colorDistance, easeOut, hashStr, luminance, smoothstep } from './util';

/*
 * Stylised low-poly athletes built from instanced rigid parts. Each part type
 * (torso, shorts, head, hair, upper arm, forearm, thigh, shin, boot, number,
 * flag) is ONE InstancedMesh shared by every figure on the field — ~11 draw
 * calls for 22 players + officials + benches. Poses are procedural: a small
 * parameter vector per figure is produced from (anim, animPhase, speed) and
 * turned into part matrices with a hand-rolled forward-kinematics pass.
 *
 * Figure-local frame: +x forward, +y up, +z = figure's right.
 * Heading convention: facing direction = (cos h, 0, sin h) in world xz,
 * i.e. h = atan2(vel.z, vel.x). (0 = facing +x, as in contracts.ts.)
 */

// Pose parameter indices
const ROOTY = 0, PITCH_ = 1, ROLL = 2, LEAN = 3, TWIST = 4, HEADP = 5,
  LHF = 6, LHA = 7, LK = 8, RHF = 9, RHA = 10, RK = 11,
  LSF = 12, LSA = 13, LE = 14, RSF = 15, RSA = 16, RE = 17, HEADY = 18;
const NP = 19;

type FigKind = 'player' | 'referee' | 'assistant' | 'bench' | 'manager';

interface Figure {
  kind: FigKind;
  team: TeamSide | null;
  id: string;
  hash: number;
  scale: number;
  isGK: boolean;
  // world state
  x: number; y: number; z: number;
  lastX: number; lastZ: number;
  heading: number;
  gait: number;
  speed: number;
  pose: Float32Array;
  target: Float32Array;
  from: Float32Array;
  blend: number;
  animKey: string;
  clock: number;
  initialised: boolean;
  /** Bench/manager home spot. */
  homeX: number; homeZ: number; homeHeading: number;
  /** bench reaction: 1 celebrate, -1 dejected, 0 none */
  react: number;
}

export interface FigureSet {
  meshes: THREE.InstancedMesh[];
  update(prev: WorldSnapshot, next: WorldSnapshot, alpha: number, dt: number, celebrating: TeamSide | null): void;
  /** Ground positions for blob shadows (x,z) — length = figureCount. */
  groundX: Float32Array; groundZ: Float32Array; lying: Float32Array;
  figureCount: number;
  setCastShadow(on: boolean): void;
  dispose(): void;
}

// ----------------------------------------------------------------- geometry
function ellipticCyl(rTop: number, rBot: number, h: number, sx: number, sz: number, y0: number, segs = 8): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(rTop, rBot, h, segs, 1, false);
  g.rotateY(Math.PI / segs);
  g.scale(sx, 1, sz);
  g.translate(0, y0 + h / 2, 0);
  return g;
}

function limb(rTop: number, rBot: number, len: number, segs = 6): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(rTop, rBot, len, segs, 1, false);
  g.translate(0, -len / 2, 0);
  return g;
}

function buildGeometries() {
  const torso = ellipticCyl(0.5, 0.41, 0.56, 0.27, 0.43, 0.0, 8);
  // shoulder yoke: make the chest broader at the top
  const pa = torso.attributes.position!;
  for (let i = 0; i < pa.count; i++) if (pa.getY(i) > 0.5) pa.setZ(i, pa.getZ(i) * 1.06);
  torso.computeVertexNormals();
  const shorts = ellipticCyl(0.44, 0.47, 0.27, 0.27, 0.43, -0.23, 8);
  const head = new THREE.SphereGeometry(0.112, 10, 7);
  head.scale(1.0, 1.12, 0.92);
  head.translate(0.01, 0.125, 0);
  const neck = limb(0.05, 0.055, 0.08, 6); neck.translate(0, 0.04, 0);
  const headG = mergeGeometries([head, neck], false)!;
  const hair = new THREE.SphereGeometry(0.12, 10, 5, 0, Math.PI * 2, 0, Math.PI * 0.42);
  hair.scale(1.02, 1.1, 0.96);
  hair.rotateZ(0.35);
  hair.translate(-0.012, 0.135, 0);
  const upperArm = limb(0.058, 0.048, 0.3, 6);
  const fore = limb(0.044, 0.038, 0.24, 6);
  const hand = new THREE.SphereGeometry(0.048, 6, 4); hand.translate(0, -0.27, 0);
  const forearm = mergeGeometries([fore, hand], false)!;
  const thigh = limb(0.085, 0.063, 0.45, 7);
  const shin = limb(0.06, 0.042, 0.42, 6);
  const boot = new THREE.BoxGeometry(0.23, 0.075, 0.1); boot.translate(0.045, -0.03, 0);
  const number = new THREE.PlaneGeometry(0.26, 0.24);
  number.rotateY(-Math.PI / 2);
  number.translate(-0.128, 0.33, 0);
  // Linesman's flag: stick + cloth (vertex coloured)
  const stick = new THREE.CylinderGeometry(0.012, 0.012, 0.55, 5); stick.translate(0, -0.2, 0);
  const cloth = new THREE.BoxGeometry(0.02, 0.3, 0.4); cloth.translate(0, -0.33, 0.2);
  const colorize = (g: THREE.BufferGeometry, c: THREE.Color) => {
    const n = g.attributes.position!.count; const a = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { a[i * 3] = c.r; a[i * 3 + 1] = c.g; a[i * 3 + 2] = c.b; }
    g.setAttribute('color', new THREE.BufferAttribute(a, 3));
  };
  colorize(stick, new THREE.Color('#202020'));
  colorize(cloth, new THREE.Color('#ffd400'));
  const flag = mergeGeometries([stick, cloth], false)!;
  for (const g of [head, neck, fore, hand, stick, cloth]) g.dispose();
  return { torso, shorts, head: headG, hair, upperArm, forearm, thigh, shin, boot, number, flag };
}

// ----------------------------------------------------------------- palettes
const SKIN = ['#f1c7a5', '#e0ac85', '#c68b5f', '#a86b43', '#7d4a2b', '#5a321c', '#3f2416', '#ecbc95'];
const HAIR = ['#1a1410', '#2b1d14', '#3d2a1a', '#6b4a2b', '#b48a5a', '#0e0e0e', '#d9c08c', '#4a3020'];

function pickRefColor(config: MatchConfig): string {
  const cands = ['#111111', '#d8f21c', '#ff3c8e', '#1fc8e6'];
  const kits = [config.home.primaryColor, config.away.primaryColor, config.home.keeperColor, config.away.keeperColor];
  let best = cands[0]!, bestScore = -1;
  for (const c of cands) {
    const sc = Math.min(...kits.map((k) => colorDistance(c, k)));
    if (sc > bestScore + 0.05) { best = c; bestScore = sc; }
  }
  return best;
}

function numberTint(shirt: string, secondary: string): THREE.Color {
  if (colorDistance(shirt, secondary) > 0.45) return new THREE.Color(secondary);
  return new THREE.Color(luminance(shirt) > 0.35 ? '#141414' : '#f5f5f5');
}

// ----------------------------------------------------------------- poses
const TAU = Math.PI * 2;

function locomotion(o: Float32Array, speed: number, gait: number, clock: number, hash: number): void {
  const t = Math.min(1, speed / 8.5);
  const walk = Math.min(1, speed / 1.6);
  const s = Math.sin(gait * TAU), c = Math.cos(gait * TAU);
  const amp = walk * (0.32 + 0.6 * t);
  o[LHF] = amp * s + 0.05 * t;
  o[RHF] = -amp * s + 0.05 * t;
  const kAmp = walk * (0.35 + 1.25 * t);
  o[LK] = 0.08 + kAmp * Math.max(0, Math.sin(gait * TAU + 1.9)) + 0.15 * t;
  o[RK] = 0.08 + kAmp * Math.max(0, Math.sin(gait * TAU + 1.9 + Math.PI)) + 0.15 * t;
  o[LHA] = 0.03; o[RHA] = 0.03;
  const arm = walk * (0.28 + 0.55 * t);
  o[LSF] = -arm * s * 0.9 + 0.05; o[RSF] = arm * s * 0.9 + 0.05;
  o[LSA] = 0.1 + 0.05 * t; o[RSA] = 0.1 + 0.05 * t;
  o[LE] = 0.25 + 1.15 * t * walk + 0.15 * walk; o[RE] = o[LE];
  o[LEAN] = 0.03 + 0.24 * t * walk;
  o[TWIST] = 0.12 * s * walk * (0.5 + t);
  o[ROOTY] = -0.015 - 0.05 * t + Math.abs(c) * 0.045 * walk * (0.4 + t);
  o[PITCH_] = 0; o[ROLL] = 0;
  o[HEADP] = -0.1 * t;
  o[HEADY] = 0;
  // idle breathing / weight shift
  const idle = 1 - walk;
  if (idle > 0) {
    const b = Math.sin(clock * 1.7 + (hash % 100)) * 0.5 + 0.5;
    o[LEAN] += idle * (0.04 + 0.015 * b);
    o[LSA] += idle * 0.06; o[RSA] += idle * 0.06;
    o[LE] += idle * 0.15; o[RE] += idle * 0.2;
    o[LHA] += idle * 0.04; o[RHA] += idle * 0.06;
    o[ROLL] += idle * 0.02 * Math.sin(clock * 0.5 + hash);
  }
}

function applyAnim(fig: Figure, o: Float32Array, anim: PlayerAnim, p: number, lateral: number, clock: number): void {
  locomotion(o, fig.speed, fig.gait, clock, fig.hash);
  const h = fig.hash;
  switch (anim) {
    case 'idle': case 'jog': case 'run': case 'sprint': return;
    case 'kick': {
      const wind = smoothstep(0, 0.35, p), strike = smoothstep(0.35, 0.55, p), follow = smoothstep(0.55, 1, p);
      o[RHF] = -0.7 * wind + 2.0 * strike - 0.9 * follow;
      o[RK] = 1.4 * wind - 1.3 * strike + 0.2 * follow;
      o[LHF] = 0.15; o[LK] = 0.25;
      o[LSA] = 0.9; o[LSF] = 0.4; o[RSA] = 0.6; o[RSF] = -0.3;
      o[LE] = 0.5; o[RE] = 0.4;
      o[LEAN] = 0.15 - 0.25 * strike + 0.1 * follow;
      o[TWIST] = -0.25 * wind + 0.4 * strike;
      o[ROOTY] = -0.04;
      return;
    }
    case 'header': {
      const j = Math.sin(Math.PI * Math.min(1, p));
      o[ROOTY] = 0.5 * j;
      o[LK] = o[RK] = 0.3 + 0.6 * j; o[LHF] = o[RHF] = 0.2 * j;
      o[LSA] = o[RSA] = 0.7; o[LSF] = o[RSF] = 0.5; o[LE] = o[RE] = 0.9;
      o[LEAN] = -0.25 + 0.6 * smoothstep(0.35, 0.6, p);
      o[HEADP] = -0.4 + 0.9 * smoothstep(0.4, 0.55, p);
      return;
    }
    case 'tackle': {
      const down = easeOut(p * 3.5), up = smoothstep(0.75, 1, p);
      const w = down * (1 - up);
      o[PITCH_] = -1.2 * w; o[ROOTY] = -0.74 * w + o[ROOTY] * (1 - w);
      o[RHF] = o[RHF] * (1 - w) + 0.35 * w; o[RK] = o[RK] * (1 - w) + 0.05 * w;
      o[LHF] = o[LHF] * (1 - w) - 0.2 * w; o[LK] = o[LK] * (1 - w) + 1.5 * w;
      o[LEAN] = 0.45 * w; o[LSA] = 1.2 * w + 0.1; o[RSA] = 0.6 * w + 0.1; o[LSF] = -0.6 * w; o[RSF] = 0.5 * w;
      o[HEADP] = 0.4 * w;
      return;
    }
    case 'dive': {
      const side = lateral !== 0 ? Math.sign(lateral) : (h & 1 ? 1 : -1);
      const lean = easeOut(p / 0.35);
      const air = Math.sin(Math.PI * Math.min(1, p / 0.7));
      const land = smoothstep(0.6, 0.85, p);
      o[ROLL] = side * 1.45 * lean;
      o[ROOTY] = 0.55 * air * (1 - land) - 0.68 * land;
      o[LSF] = o[RSF] = 2.9 * lean; o[LSA] = o[RSA] = 0.25;
      o[LE] = o[RE] = 0.15;
      o[LHF] = 0.25; o[RHF] = -0.1; o[LK] = 0.5; o[RK] = 0.2;
      o[LEAN] = 0.05; o[TWIST] = 0;
      return;
    }
    case 'fall': {
      const dir = h & 2 ? 1 : -1;
      const w = easeOut(p * 2.2);
      o[PITCH_] = dir * 1.45 * w;
      o[ROOTY] = -0.78 * w + o[ROOTY] * (1 - w);
      o[LSF] = o[RSF] = dir > 0 ? 1.6 * w : 0.6 * w; o[LSA] = o[RSA] = 0.5 * w;
      o[LE] = o[RE] = 0.6 * w;
      o[LK] = o[LK] * (1 - w) + 0.5 * w; o[RK] = o[RK] * (1 - w) + 0.2 * w;
      o[LHF] = o[LHF] * (1 - w) + 0.1 * w; o[RHF] = o[RHF] * (1 - w) + 0.1 * w;
      o[HEADP] = -0.3 * w * dir;
      return;
    }
    case 'celebrate': {
      const v = h % 3;
      if (v === 1 && fig.speed < 3.5) {
        // knee slide / kneel, arms wide, head back
        o[LHF] = o[RHF] = -0.05; o[LK] = o[RK] = 1.62; o[LHA] = o[RHA] = 0.18;
        o[ROOTY] = -0.42; o[LEAN] = -0.45; o[HEADP] = -0.5;
        o[LSA] = o[RSA] = 1.55 + 0.1 * Math.sin(clock * 6); o[LSF] = o[RSF] = 0.4; o[LE] = o[RE] = 0.2;
        o[TWIST] = 0;
      } else if (v === 2) {
        // aeroplane
        o[LSA] = o[RSA] = 1.5; o[LSF] = o[RSF] = 0.1; o[LE] = o[RE] = 0.05;
        o[ROLL] = 0.3 * Math.sin(clock * 2.2 + h);
      } else {
        // fist pumps + hops
        const pump = Math.sin(clock * 9 + h) * 0.5 + 0.5;
        o[LSF] = 2.7 + 0.25 * pump; o[RSF] = 2.5 + 0.3 * (1 - pump);
        o[LSA] = o[RSA] = 0.35; o[LE] = 0.6 + 0.6 * pump; o[RE] = 0.6 + 0.6 * (1 - pump);
        if (fig.speed < 2) { o[ROOTY] += 0.14 * Math.max(0, Math.sin(clock * 8 + h)); o[LK] = o[RK] = 0.3; }
        o[HEADP] = -0.35;
      }
      return;
    }
    case 'dejected': {
      if (h % 2 === 0) {
        // hands on head
        o[LSF] = o[RSF] = 2.55; o[LSA] = o[RSA] = -0.15; o[LE] = o[RE] = 2.1;
        o[HEADP] = 0.15;
      } else {
        // hands on hips, head down
        o[LSF] = o[RSF] = -0.15; o[LSA] = o[RSA] = 0.65; o[LE] = o[RE] = 1.6;
        o[HEADP] = 0.55; o[LEAN] = 0.12;
      }
      return;
    }
    case 'throw': {
      const back = smoothstep(0, 0.45, p), fwd = smoothstep(0.45, 0.7, p);
      o[LSF] = o[RSF] = 2.6 + 0.6 * back - 1.2 * fwd;
      o[LSA] = o[RSA] = -0.1;
      o[LE] = o[RE] = 0.4 + 1.3 * back - 1.4 * fwd;
      o[LEAN] = -0.35 * back + 0.5 * fwd;
      o[LHF] = 0.25; o[RHF] = -0.2; o[LK] = 0.2; o[RK] = 0.25;
      return;
    }
  }
}

function sitPose(o: Float32Array, clock: number, hash: number): void {
  o.fill(0);
  o[ROOTY] = -0.37; o[LHF] = o[RHF] = 1.5; o[LK] = o[RK] = 1.45; o[LHA] = 0.12; o[RHA] = 0.14;
  o[LSF] = 0.55; o[RSF] = 0.5; o[LE] = 0.9; o[RE] = 1.0; o[LSA] = 0.12; o[RSA] = 0.12;
  o[LEAN] = 0.18 + 0.03 * Math.sin(clock * 0.7 + hash);
  o[HEADP] = -0.05;
}

const animClass = (a: PlayerAnim): string => (a === 'idle' || a === 'jog' || a === 'run' || a === 'sprint' ? 'loco' : a);
const LOOPING: Record<PlayerAnim, boolean> = { idle: true, jog: true, run: true, sprint: true, kick: false, header: false, tackle: false, dive: false, fall: false, celebrate: true, dejected: true, throw: false };

// ----------------------------------------------------------------- build
export function buildFigures(config: MatchConfig): FigureSet {
  const geos = buildGeometries();
  const figs: Figure[] = [];
  const newFig = (kind: FigKind, team: TeamSide | null, id: string, isGK = false): Figure => {
    const h = hashStr(id);
    const f: Figure = {
      kind, team, id, hash: h, isGK, scale: 0.95 + ((h >>> 8) % 100) / 1000,
      x: 0, y: 0, z: 0, lastX: 0, lastZ: 0, heading: 0, gait: (h % 97) / 97, speed: 0,
      pose: new Float32Array(NP), target: new Float32Array(NP), from: new Float32Array(NP),
      blend: 1, animKey: 'loco', clock: (h % 1000) / 100, initialised: false,
      homeX: 0, homeZ: 0, homeHeading: Math.PI / 2, react: 0,
    };
    figs.push(f);
    return f;
  };
  const profiles = new Map<string, { team: TeamSide; isGK: boolean; number: number }>();
  for (const team of [config.home, config.away]) {
    team.players.forEach((pl, i) => profiles.set(pl.id, { team: team.side, isGK: pl.position === 'GK' || i === 0, number: pl.number }));
  }
  // 22 players: order assigned on first snapshot, pre-created in config order
  const playerFigs: Figure[] = [];
  for (const team of [config.home, config.away]) for (let i = 0; i < 11; i++) {
    const pl = team.players[i];
    const id = pl ? pl.id : `${team.side}-${i}`;
    playerFigs.push(newFig('player', team.side, id, pl ? (pl.position === 'GK' || i === 0) : i === 0));
  }
  const referee = newFig('referee', null, 'referee');
  const ar1 = newFig('assistant', null, 'assistant1');
  const ar2 = newFig('assistant', null, 'assistant2');
  // benches: 6 seated + manager per side
  const benchFigs: Figure[] = [];
  for (const team of [config.home, config.away]) {
    const sx = team.side === 'home' ? -1 : 1;
    for (let k = 0; k < 6; k++) {
      const b = team.bench[k];
      const f = newFig('bench', team.side, b ? b.id : `${team.side}-bench-${k}`);
      f.homeX = sx * DUGOUT.x - DUGOUT.len / 2 + 0.6 + (k + (k >= 3 ? 1 : 0)) * ((DUGOUT.len - 1.2) / 6);
      f.homeZ = DUGOUT.z - 0.62; f.homeHeading = Math.PI / 2;
      benchFigs.push(f);
    }
    const m = newFig('manager', team.side, `${team.side}-manager-${team.manager}`);
    m.homeX = sx * DUGOUT.x + sx * 1.5; m.homeZ = -36.2; m.homeHeading = Math.PI / 2;
    benchFigs.push(m);
  }
  const N = figs.length;

  // per-figure colours
  const refColor = pickRefColor(config);
  const C = (h: string) => new THREE.Color(h);
  const colShirt: THREE.Color[] = [], colShorts: THREE.Color[] = [], colSocks: THREE.Color[] = [], colSkin: THREE.Color[] = [],
    colHair: THREE.Color[] = [], colFore: THREE.Color[] = [], colBoot: THREE.Color[] = [], colThigh: THREE.Color[] = [];
  const hasHair: boolean[] = [];
  const colTrim: THREE.Color[] = [];
  const hairStyle: THREE.Matrix4[] = [];
  const headC = new THREE.Vector3(0.01, 0.125, 0);
  const styleMat = (sx: number, sy: number, sz: number, tilt: number, dy: number) => new THREE.Matrix4()
    .makeTranslation(headC.x, headC.y + dy, headC.z)
    .multiply(new THREE.Matrix4().makeRotationZ(tilt))
    .multiply(new THREE.Matrix4().makeScale(sx, sy, sz))
    .multiply(new THREE.Matrix4().makeTranslation(-headC.x, -headC.y, -headC.z));
  for (const f of figs) {
    const skin = C(SKIN[f.hash % SKIN.length]!);
    const hair = C(HAIR[(f.hash >>> 4) % HAIR.length]!);
    const hs = (f.hash >>> 12) % 10;
    hasHair.push(hs !== 0);
    // 1-2 buzz cut, 3 big/afro, 4 longer at the back, else regular crop
    hairStyle.push(hs === 1 || hs === 2 ? styleMat(0.99, 0.82, 0.99, 0, -0.012)
      : hs === 3 ? styleMat(1.2, 1.18, 1.2, 0, 0.006)
      : hs === 4 ? styleMat(1.04, 1.06, 1.04, 0.28, -0.006)
      : new THREE.Matrix4());
    let shirt: THREE.Color, shorts: THREE.Color, socks: THREE.Color, fore: THREE.Color = skin, thigh: THREE.Color = skin;
    let boot = C(['#111111', '#f2f2f2', '#ff3b30', '#ffcc00', '#1f6bff', '#00d084'][(f.hash >>> 6) % 6]!);
    const team = f.team === 'home' ? config.home : f.team === 'away' ? config.away : null;
    if (f.kind === 'player' && team) {
      if (f.isGK) {
        shirt = C(team.keeperColor); shorts = C(team.keeperColor).multiplyScalar(0.55); socks = C(team.keeperColor);
        fore = shirt;
      } else {
        shirt = C(team.primaryColor); shorts = C(team.secondaryColor); socks = C(team.primaryColor);
        if (colorDistance(team.primaryColor, team.secondaryColor) < 0.2) shorts = C('#f0f0f0');
      }
    } else if (f.kind === 'referee' || f.kind === 'assistant') {
      shirt = C(refColor); shorts = C('#111111'); socks = C('#111111'); boot = C('#111111');
    } else if (f.kind === 'bench' && team) {
      shirt = C(team.secondaryColor).lerp(C('#1c2230'), 0.35); shorts = C('#1c2230'); socks = C('#1c2230'); fore = shirt; thigh = shorts;
    } else {
      shirt = C('#20242c'); shorts = C('#15181d'); socks = C('#15181d'); fore = shirt; thigh = shorts; boot = C('#0b0b0b');
    }
    // kit trim (collar, cuffs, side panels, sock tops)
    let trim: THREE.Color;
    if (f.kind === 'player' && team) {
      if (f.isGK) trim = shirt.clone().multiplyScalar(0.35);
      else trim = colorDistance(team.primaryColor, team.secondaryColor) > 0.25 ? C(team.secondaryColor)
        : C(luminance(team.primaryColor) > 0.35 ? '#161616' : '#f2f2f2');
    } else if (f.kind === 'referee' || f.kind === 'assistant') trim = C('#0c0c0c');
    else if (f.kind === 'manager') trim = C('#e6e6e6');
    else trim = shirt.clone().multiplyScalar(0.6);
    colTrim.push(trim);
    colShirt.push(shirt); colShorts.push(shorts); colSocks.push(socks); colSkin.push(skin); colHair.push(hair);
    colFore.push(fore); colBoot.push(boot); colThigh.push(thigh);
  }

  const kitMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.62, metalness: 0 });
  const skinMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.75, metalness: 0 });
  // Trim / face detail: per-fragment from the part's local position + an instanced colour.
  const trimMat = (mode: 'torso' | 'arm' | 'shin' | 'head', base: THREE.MeshStandardMaterial) => {
    const mat = base.clone();
    const rule = {
      torso: `if (vLocalP.y > 0.5) c = vTrim;
              else if (abs(vLocalP.z) > 0.195 && vLocalP.y > 0.06) c = mix(c, vTrim, 0.8);
              if (vLocalP.y < 0.05) c *= 0.82;`,
      arm: 'if (vLocalP.y < -0.245) c = vTrim;',
      shin: 'if (vLocalP.y > -0.075) c = vTrim; else if (vLocalP.y > -0.1) c *= 0.8;',
      head: `if (vLocalP.y > 0.04) {
               vec2 yz = vLocalP.yz;
               float front = smoothstep(0.06, 0.09, vLocalP.x);
               float eye = min(length(yz - vec2(0.142, 0.037)), length(yz - vec2(0.142, -0.037)));
               c *= 1.0 - front * 0.75 * (1.0 - smoothstep(0.011, 0.019, eye));
               float brow = step(0.158, yz.x) * step(yz.x, 0.172) * step(0.014, abs(yz.y)) * step(abs(yz.y), 0.058);
               c = mix(c, vTrim * 1.4, front * brow * 0.85);
               float mouth = (1.0 - smoothstep(0.004, 0.009, abs(yz.x - 0.078))) * step(abs(yz.y), 0.026);
               c *= 1.0 - front * mouth * 0.45;
               c *= 1.0 - 0.18 * (1.0 - smoothstep(0.03, 0.07, vLocalP.y));
             }`,
    }[mode];
    mat.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nattribute vec3 aTrim;\nvarying vec3 vTrim;\nvarying vec3 vLocalP;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvTrim = aTrim; vLocalP = position;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vTrim;\nvarying vec3 vLocalP;')
        .replace('#include <color_fragment>', `#include <color_fragment>\n{ vec3 c = diffuseColor.rgb; ${rule} diffuseColor.rgb = c; }`);
    };
    mat.customProgramCacheKey = () => `sd-fig-trim-${mode}-v1`;
    return mat;
  };
  const torsoMat = trimMat('torso', kitMat), armMat = trimMat('arm', kitMat), shinMat = trimMat('shin', kitMat), headMat = trimMat('head', skinMat);
  const atlas = numberAtlas();
  const numMat = new THREE.MeshStandardMaterial({ color: 0xffffff, map: atlas, alphaTest: 0.45, roughness: 0.6, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 });
  const numberCells = new Float32Array(22);
  numMat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute float aCell;')
      .replace('#include <uv_vertex>', '#include <uv_vertex>\nvMapUv = (uv + vec2(mod(aCell, 10.0), 9.0 - floor(aCell / 10.0))) / 10.0;');
  };
  numMat.customProgramCacheKey = () => 'sd-number-v1';
  const flagMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.7, side: THREE.DoubleSide });

  const mk = (name: string, g: THREE.BufferGeometry, mat: THREE.Material, count: number) => {
    const m = new THREE.InstancedMesh(g, mat, count);
    m.name = `fig-${name}`;
    m.frustumCulled = false;
    m.matrixAutoUpdate = false;
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    return m;
  };
  const mTorso = mk('torso', geos.torso, torsoMat, N);
  const mShorts = mk('shorts', geos.shorts, kitMat, N);
  const mHead = mk('head', geos.head, headMat, N);
  const mHair = mk('hair', geos.hair, skinMat, N);
  const mUpper = mk('upperArm', geos.upperArm, armMat, N * 2);
  const mFore = mk('forearm', geos.forearm, skinMat, N * 2);
  const mThigh = mk('thigh', geos.thigh, skinMat, N * 2);
  const mShin = mk('shin', geos.shin, shinMat, N * 2);
  const mBoot = mk('boot', geos.boot, kitMat, N * 2);
  const mNum = mk('number', geos.number, numMat, 22);
  const mFlag = mk('flag', geos.flag, flagMat, 2);
  for (let i = 0; i < N; i++) {
    mTorso.setColorAt(i, colShirt[i]!); mShorts.setColorAt(i, colShorts[i]!); mHead.setColorAt(i, colSkin[i]!); mHair.setColorAt(i, colHair[i]!);
    for (let s = 0; s < 2; s++) {
      mUpper.setColorAt(i * 2 + s, colShirt[i]!); mFore.setColorAt(i * 2 + s, colFore[i]!);
      mThigh.setColorAt(i * 2 + s, colThigh[i]!); mShin.setColorAt(i * 2 + s, colSocks[i]!); mBoot.setColorAt(i * 2 + s, colBoot[i]!);
    }
  }
  for (let i = 0; i < 22; i++) {
    const f = playerFigs[i]!;
    const team = f.team === 'home' ? config.home : config.away;
    const prof = profiles.get(f.id);
    numberCells[i] = Math.max(0, Math.min(99, prof ? prof.number : (i % 11) + 1));
    const shirt = f.isGK ? team.keeperColor : team.primaryColor;
    mNum.setColorAt(i, numberTint(shirt, f.isGK ? '#111111' : team.secondaryColor));
  }
  geos.number.setAttribute('aCell', new THREE.InstancedBufferAttribute(numberCells, 1));
  const trim1 = new Float32Array(N * 3), trim2 = new Float32Array(N * 6), hairC = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    colTrim[i]!.toArray(trim1, i * 3);
    colTrim[i]!.toArray(trim2, i * 6); colTrim[i]!.toArray(trim2, i * 6 + 3);
    colHair[i]!.toArray(hairC, i * 3);
  }
  geos.torso.setAttribute('aTrim', new THREE.InstancedBufferAttribute(trim1, 3));
  geos.upperArm.setAttribute('aTrim', new THREE.InstancedBufferAttribute(trim2, 3));
  geos.shin.setAttribute('aTrim', new THREE.InstancedBufferAttribute(trim2.slice(), 3));
  // brows take the hair colour (bald heads: a dark brow)
  for (let i = 0; i < N; i++) if (!hasHair[i]) hairC.set([0.02, 0.015, 0.01], i * 3);
  geos.head.setAttribute('aTrim', new THREE.InstancedBufferAttribute(hairC, 3));
  const meshes = [mTorso, mShorts, mHead, mHair, mUpper, mFore, mThigh, mShin, mBoot, mNum, mFlag];
  for (const m of meshes) if (m.instanceColor) m.instanceColor.needsUpdate = true;

  // ----------------------------------------------------------------- FK
  const _root = new THREE.Matrix4(), _torso = new THREE.Matrix4(), _tmp = new THREE.Matrix4(), _a = new THREE.Matrix4(), _b = new THREE.Matrix4();
  const _e = new THREE.Euler(), _q = new THREE.Quaternion(), _p = new THREE.Vector3(), _s = new THREE.Vector3();
  const local = (out: THREE.Matrix4, parent: THREE.Matrix4, ox: number, oy: number, oz: number, rx: number, ry: number, rz: number) => {
    _e.set(rx, ry, rz, 'XYZ');
    _tmp.makeRotationFromEuler(_e);
    _tmp.elements[12] = ox; _tmp.elements[13] = oy; _tmp.elements[14] = oz;
    out.multiplyMatrices(parent, _tmp);
  };
  const zero = new THREE.Matrix4().makeScale(0, 0, 0);
  const HIP_Y = 0.93;

  const writePose = (i: number, f: Figure) => {
    const o = f.pose;
    _e.set(o[ROLL]!, -f.heading, -o[PITCH_]!, 'YXZ');
    _q.setFromEuler(_e);
    _p.set(f.x, f.y + (HIP_Y + o[ROOTY]!) * f.scale, f.z);
    _s.set(f.scale, f.scale, f.scale);
    _root.compose(_p, _q, _s);
    mShorts.setMatrixAt(i, _root);
    local(_torso, _root, 0, 0.0, 0, 0, o[TWIST]!, -o[LEAN]!);
    mTorso.setMatrixAt(i, _torso);
    if (i < 22) mNum.setMatrixAt(i, _torso);
    local(_a, _torso, 0, 0.56, 0, 0, o[HEADY]! - o[TWIST]! * 0.5, -o[HEADP]!);
    mHead.setMatrixAt(i, _a);
    if (hasHair[i]) { _b.multiplyMatrices(_a, hairStyle[i]!); mHair.setMatrixAt(i, _b); } else mHair.setMatrixAt(i, zero);
    for (let s = 0; s < 2; s++) {
      const side = s === 0 ? -1 : 1;
      const sf = o[s === 0 ? LSF : RSF]!, sa = o[s === 0 ? LSA : RSA]!, el = o[s === 0 ? LE : RE]!;
      local(_a, _torso, 0, 0.5, side * 0.215, -side * sa, 0, sf);
      mUpper.setMatrixAt(i * 2 + s, _a);
      local(_b, _a, 0, -0.29, 0, 0, 0, el);
      mFore.setMatrixAt(i * 2 + s, _b);
      if (f.kind === 'assistant' && s === 1) {
        local(_a, _b, 0, -0.26, 0, 0, 0, 0);
        mFlag.setMatrixAt(f === ar1 ? 0 : 1, _a);
      }
      const hf = o[s === 0 ? LHF : RHF]!, ha = o[s === 0 ? LHA : RHA]!, kn = o[s === 0 ? LK : RK]!;
      local(_a, _root, 0, -0.06, side * 0.1, -side * ha, 0, hf);
      mThigh.setMatrixAt(i * 2 + s, _a);
      local(_b, _a, 0, -0.44, 0, 0, 0, -kn);
      mShin.setMatrixAt(i * 2 + s, _b);
      local(_a, _b, 0, -0.42, 0, 0, 0, (kn - hf) * 0.55);
      mBoot.setMatrixAt(i * 2 + s, _a);
    }
  };

  const groundX = new Float32Array(N), groundZ = new Float32Array(N), lying = new Float32Array(N);
  let idsMapped = false;
  const playerIndex: number[] = new Array(22).fill(-1);
  const tmpPose = new Float32Array(NP);

  const placeFigure = (f: Figure, x: number, z: number, heading: number, snap: boolean, dt: number) => {
    const moved = Math.hypot(x - f.lastX, z - f.lastZ);
    if (!f.initialised || moved > 6) snap = true;
    f.x = x; f.z = z;
    if (snap) { f.heading = heading; f.lastX = x; f.lastZ = z; f.initialised = true; return 0; }
    f.heading = angleLerp(f.heading, heading, damp(12, dt));
    f.lastX = x; f.lastZ = z;
    return moved;
  };

  const advance = (f: Figure, moved: number, speed: number, dt: number) => {
    f.speed = speed;
    const stride = 1.35 + speed * 0.3;
    f.gait = (f.gait + moved / stride) % 1;
    f.clock += dt;
  };

  const setTarget = (f: Figure, key: string, dt: number) => {
    if (key !== f.animKey) {
      f.from.set(f.pose);
      f.blend = 0;
      f.animKey = key;
    }
    f.blend = Math.min(1, f.blend + dt / 0.16);
    const w = f.blend * f.blend * (3 - 2 * f.blend);
    for (let k = 0; k < NP; k++) f.pose[k] = f.from[k]! + (f.target[k]! - f.from[k]!) * w;
  };

  const ballPos = { x: 0, y: 0, z: 0 } as Vec3;

  const set: FigureSet = {
    meshes,
    groundX, groundZ, lying,
    figureCount: N,
    setCastShadow(on: boolean) { for (const m of meshes) m.castShadow = on; },
    update(prev, next, alpha, dt, celebrating) {
      dt = Math.max(0, Math.min(dt, 0.25));
      const snapDt = next.t - prev.t;
      ballPos.x = prev.ball.pos.x + (next.ball.pos.x - prev.ball.pos.x) * alpha;
      ballPos.z = prev.ball.pos.z + (next.ball.pos.z - prev.ball.pos.z) * alpha;
      if (!idsMapped) {
        for (let i = 0; i < Math.min(22, next.players.length); i++) {
          const id = next.players[i]!.id;
          let idx = playerFigs.findIndex((f) => f.id === id);
          if (idx < 0) {
            // fall back to team order
            const side = next.players[i]!.team;
            idx = playerFigs.findIndex((f, k) => f.team === side && !playerIndex.includes(k));
          }
          playerIndex[i] = idx;
        }
        idsMapped = true;
      }
      // ---- players
      for (let i = 0; i < Math.min(22, next.players.length); i++) {
        const fi = playerIndex[i]!;
        if (fi < 0) continue;
        const f = playerFigs[fi]!;
        const b = next.players[i]!;
        let a: PlayerState = prev.players[i]!;
        if (!a || a.id !== b.id) a = prev.players.find((p) => p.id === b.id) ?? b;
        const jump = Math.hypot(b.pos.x - a.pos.x, b.pos.z - a.pos.z) > 8;
        const t = jump ? 1 : alpha;
        const x = a.pos.x + (b.pos.x - a.pos.x) * t, z = a.pos.z + (b.pos.z - a.pos.z) * t;
        f.y = Math.max(0, a.pos.y + (b.pos.y - a.pos.y) * t);
        const vx = a.vel.x + (b.vel.x - a.vel.x) * t, vz = a.vel.z + (b.vel.z - a.vel.z) * t;
        let speed = Math.hypot(vx, vz);
        if (speed < 0.01 && snapDt > 1e-4) speed = Math.hypot(b.pos.x - a.pos.x, b.pos.z - a.pos.z) / snapDt;
        const heading = jump ? b.heading : angleLerp(a.heading, b.heading, t);
        const moved = placeFigure(f, x, z, heading, jump, dt);
        advance(f, moved, speed, dt);
        const src = t < 0.5 ? a : b;
        const anim = src.anim;
        let phase = src.animPhase;
        if (a.anim === b.anim && !jump) {
          let pb = b.animPhase;
          if (LOOPING[a.anim] && pb < a.animPhase - 0.5) pb += 1;
          phase = (a.animPhase + (pb - a.animPhase) * t) % 1.000001;
        }
        const rx = -Math.sin(f.heading), rz = Math.cos(f.heading);
        const lateral = vx * rx + vz * rz;
        applyAnim(f, f.target, anim, phase, Math.abs(lateral) > 0.4 ? lateral : 0, f.clock);
        setTarget(f, animClass(anim), dt);
        if (jump) { f.pose.set(f.target); f.blend = 1; }
        writePose(fi, f);
        groundX[fi] = f.x; groundZ[fi] = f.z;
        lying[fi] = Math.min(1, Math.abs(f.pose[PITCH_]!) + Math.abs(f.pose[ROLL]!) * 0.7);
      }
      // ---- officials
      const off = (f: Figure, pa: Vec3, pb: Vec3, isAR: boolean) => {
        const jump = Math.hypot(pb.x - pa.x, pb.z - pa.z) > 10;
        const t = jump ? 1 : alpha;
        const x = pa.x + (pb.x - pa.x) * t, z = pa.z + (pb.z - pa.z) * t;
        const speed = snapDt > 1e-4 && !jump ? Math.hypot(pb.x - pa.x, pb.z - pa.z) / snapDt : 0;
        let heading: number;
        const toBall = Math.atan2(ballPos.z - z, ballPos.x - x);
        if (isAR) heading = toBall;
        else if (speed > 2.2) heading = Math.atan2(pb.z - pa.z, pb.x - pa.x);
        else heading = toBall;
        const moved = placeFigure(f, x, z, heading, jump, dt);
        advance(f, moved, speed, dt);
        f.y = 0;
        applyAnim(f, f.target, 'idle', 0, 0, f.clock);
        if (isAR) { f.target[RSF] = 0.25; f.target[RE] = 0.5; f.target[RSA] = 0.15; }
        setTarget(f, 'loco', dt);
        const i = figs.indexOf(f);
        writePose(i, f);
        groundX[i] = f.x; groundZ[i] = f.z; lying[i] = 0;
      };
      const pOff = prev.officials, nOff = next.officials;
      off(referee, pOff.referee, nOff.referee, false);
      off(ar1, pOff.assistant1, nOff.assistant1, true);
      off(ar2, pOff.assistant2, nOff.assistant2, true);
      // ---- benches + managers
      for (const f of benchFigs) {
        const i = figs.indexOf(f);
        f.clock += dt;
        const react = celebrating === null ? 0 : celebrating === f.team ? 1 : -1;
        const toBall = Math.atan2(ballPos.z - f.homeZ, ballPos.x - f.homeX);
        let headingTarget = f.homeHeading;
        if (f.kind === 'manager') {
          headingTarget = toBall;
          f.speed = 0;
          applyAnim(f, tmpPose, react > 0 ? 'celebrate' : react < 0 ? 'dejected' : 'idle', 0, 0, f.clock);
          // occasional touchline gesture
          const g = Math.sin(f.clock * 0.6 + f.hash) > 0.75 && react === 0 ? 1 : 0;
          if (g) { tmpPose[RSF] = 1.4 + 0.3 * Math.sin(f.clock * 5); tmpPose[RSA] = 0.6; tmpPose[RE] = 0.3; }
          f.target.set(tmpPose);
          setTarget(f, react > 0 ? 'cel' : react < 0 ? 'dej' : 'idle', dt);
        } else {
          if (react > 0) {
            f.speed = 0;
            applyAnim(f, f.target, 'celebrate', 0, 0, f.clock);
            f.target[LK] = f.target[RK] = 0.2; f.target[LHF] = f.target[RHF] = 0;
            f.target[ROOTY] = Math.max(0, f.target[ROOTY]!) + 0.12 * Math.max(0, Math.sin(f.clock * 7 + f.hash));
            f.target[LEAN] = 0; f.target[PITCH_] = 0; f.target[ROLL] = 0;
            setTarget(f, 'cel', dt);
          } else {
            sitPose(f.target, f.clock, f.hash);
            f.target[HEADY] = Math.max(-0.9, Math.min(0.9, -angleLerp(0, toBall - f.homeHeading, 1)));
            if (react < 0) { f.target[HEADP] = 0.45; f.target[LEAN] = 0.45; }
            setTarget(f, react < 0 ? 'dej' : 'sit', dt);
          }
        }
        f.x = f.homeX; f.z = f.homeZ; f.y = f.kind === 'bench' ? 0.12 : 0;
        f.heading = f.initialised ? angleLerp(f.heading, headingTarget, damp(4, dt)) : headingTarget;
        f.initialised = true;
        writePose(i, f);
        groundX[i] = f.x; groundZ[i] = f.z; lying[i] = f.kind === 'bench' ? -1 : 0;
      }
      for (const m of meshes) m.instanceMatrix.needsUpdate = true;
    },
    dispose() {
      for (const m of meshes) { m.geometry.dispose(); m.dispose(); }
      kitMat.dispose(); skinMat.dispose(); numMat.dispose(); torsoMat.dispose(); armMat.dispose(); shinMat.dispose(); headMat.dispose(); flagMat.dispose(); atlas.dispose();
    },
  };
  // Initialise matrices so nothing renders at the origin before the first update.
  for (let i = 0; i < N; i++) { mTorso.setMatrixAt(i, zero); }
  return set;
}
